/**
 * EmailModal Component
 * Modal for admin to email widget proposers
 * Matches the design from the main website's "Join Our Development Community" modal
 * Created: January 2025
 */

import React, { useState } from 'react';
import './EmailModal.css';

const EmailModal = ({ isOpen, onClose, widgetRequest }) => {
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    emailType: 'question' // question, feedback, update, clarification
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Pre-populate subject based on widget request
  React.useEffect(() => {
    if (isOpen && widgetRequest) {
      const defaultSubject = `Re: Your Widget Request - ${widgetRequest.description?.substring(0, 50)}${widgetRequest.description?.length > 50 ? '...' : ''}`;
      setFormData(prev => ({
        ...prev,
        subject: defaultSubject
      }));
    }
  }, [isOpen, widgetRequest]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const response = await fetch('/api/widget-requests/admin/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          request_id: widgetRequest.id,
          recipient_email: widgetRequest.user_email,
          recipient_name: widgetRequest.user_name,
          subject: formData.subject,
          message: formData.message,
          email_type: formData.emailType,
          widget_description: widgetRequest.description
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setSent(true);
        setTimeout(() => {
          onClose();
          setSent(false);
          setFormData({
            subject: '',
            message: '',
            emailType: 'question'
          });
        }, 2000);
      } else {
        throw new Error(data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert(`Error sending email: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="email-modal-overlay" onClick={onClose}>
      <div className="email-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="email-modal-header">
          <h2 className="email-modal-title">📧 Email Widget Proposer</h2>
          <button className="email-modal-close" onClick={onClose}>×</button>
        </div>

        {sent ? (
          // Success State
          <div className="email-success-state">
            <div className="success-icon">✅</div>
            <h3>Email Sent Successfully!</h3>
            <p>Your message has been sent to {widgetRequest?.user_name} ({widgetRequest?.user_email})</p>
          </div>
        ) : (
          // Email Form
          <form onSubmit={handleSubmit} className="email-form">
            {/* Recipient Info */}
            <div className="recipient-info">
              <h4>📤 Sending to:</h4>
              <div className="recipient-details">
                <span className="recipient-name">{widgetRequest?.user_name}</span>
                <span className="recipient-email">({widgetRequest?.user_email})</span>
              </div>
              <div className="widget-context">
                <strong>Widget Request:</strong> {widgetRequest?.description}
              </div>
            </div>

            {/* Email Type */}
            <div className="form-group">
              <label htmlFor="emailType">Email Type</label>
              <select
                id="emailType"
                name="emailType"
                value={formData.emailType}
                onChange={handleInputChange}
                required
              >
                <option value="question">❓ Question about request</option>
                <option value="feedback">💬 Feedback on request</option>
                <option value="update">📢 Status update</option>
                <option value="clarification">🔍 Need clarification</option>
              </select>
            </div>

            {/* Subject */}
            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                placeholder="Email subject line..."
              />
            </div>

            {/* Message */}
            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows="8"
                placeholder="Share your thoughts, questions, or feedback about their widget request..."
              />
            </div>

            {/* Action Buttons */}
            <div className="email-modal-actions">
              <button type="button" onClick={onClose} className="cancel-btn">
                Cancel
              </button>
              <button type="submit" disabled={sending} className="send-btn">
                {sending ? (
                  <>
                    <span className="sending-spinner"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    📧 Send Email
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EmailModal;
